// FNP-1192 — the record read path, plus the connection capability snapshot the
// write controls and the type picker both depend on.

// ⚠️ EVERY FIELD ON `DnsRecord` IS SELECTED, AND `isPointer` IS NOT OPTIONAL.
//
// `values` already holds the RESOLVED POINTER TARGET where a record points at
// another resource — the adapter put it there (FNP-1184). Selecting `values`
// without `isPointer` would render the target correctly and then have no way to
// say it IS a target: the row would look like ordinary rdata, invite an edit,
// and that edit would convert an alias into a literal record holding today's
// resolved address.
//
// ⚠️ THERE IS NO `id` AND NO `target` FIELD TO SELECT. A record's identity is
// (name, type) within its zone, and the pointer target arrives in `values`.
// Asking for either would be a field that is not on the schema — a validation
// error at runtime, not a TODO.
const DNS_RECORD_FIELDS = `
    name
    type
    ttlSeconds
    values
    isPointer
`;

// ⚠️ BOTH ID ARGUMENTS ARE REQUIRED (`ID!`), and the filter is SERVER-SIDE.
//
// The filter is deliberately narrow — exact `name` and exact `type` only —
// because that is all every provider can do uniformly. A `contains` would be
// served by one provider server-side and by Flexi client-side for another, so
// identical arguments would page differently depending on the provider.
export const listDnsRecords = `
   query listDnsRecords($connectionId: ID!, $zoneId: ID!, $filter: DnsRecordsFilterInput, $limit: Int, $nextToken: String) {
       listDnsRecords(connectionId: $connectionId, zoneId: $zoneId, filter: $filter, limit: $limit, nextToken: $nextToken) {
           items {${DNS_RECORD_FIELDS}}
           nextToken
       }
   }
`;

// The connection's capability snapshot. Two jobs, one query:
//
//  1. `canWriteRecords` decides whether the write controls exist at all.
//  2. `supportedRecordTypes` is what the create form's type picker offers, so
//     an unsupported type is never offered rather than being offered and then
//     rejected by the provider.
//
// ⚠️ `status` IS SELECTED TOO. The snapshot records what the credential proved
// at CONNECT time; the status is the provider saying whether it works NOW. A
// connection whose status is not ACTIVE must not get write controls whatever
// its stored snapshot claims.
//
// `secretRef` is deliberately NOT selected: this module never needs it, and not
// asking is one fewer place it could be logged.
export const getProviderConnectionCapabilities = `
   query getProviderConnection($id: ID!) {
       getProviderConnection(id: $id) {
           id
           status
           capabilities {
               supportedRecordTypes
               canWriteRecords
               canCreateZones
               canDeleteZones
               canSetNameservers
           }
       }
   }
`;

// The zone this module's records belong to, read only for its NAME.
//
// The page title and the delete confirmation both name the zone, and a record
// list headed by an opaque zone id tells an operator nothing about which zone
// they are editing — which is exactly the misread that makes a destructive
// action dangerous.
export const getDnsZone = `
   query getDnsZone($connectionId: ID!, $id: ID!) {
       getDnsZone(connectionId: $connectionId, id: $id) {
           id
           name
           connectionId
           provider
           nameservers
       }
   }
`;
